import { Icon } from '@iconify/react';
import React, { useState, useEffect, useCallback } from 'react';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const UserManagementLayer = () => {
    const { auth } = useAuth();
    const [b2cUsers, setB2cUsers] = useState([]);
    const [b2bUsers, setB2bUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/users`);
            const data = await response.json();
            setB2cUsers(data.b2c);
            setB2bUsers(data.b2b);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleBan = async (user) => {
        const isBanned = user.status === 'banned';
        const action = isBanned ? 'unban' : 'ban';
        const confirmMsg = isBanned 
            ? 'Are you sure you want to unban this user?' 
            : 'Are you sure you want to ban this user? They will be logged out immediately.';

        if (window.confirm(confirmMsg)) {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/users/${user.id}/${action}`, {
                    method: 'PUT'
                });
                if (response.ok) {
                    alert(`User ${isBanned ? 'unbanned' : 'banned'} successfully`);
                    fetchUsers();
                } else {
                    const data = await response.json();
                    alert(data.error || `Failed to ${action} user`);
                }
            } catch (error) {
                console.error(`Error ${action}ning user:`, error);
                alert(`An error occurred while ${action}ning the user`);
            }
        }
    };

    const handleDelete = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            try {
                const response = await auth.fetch(`${config.API_BASE_URL}/api/users/${userId}`, {
                    method: 'DELETE'
                });
                if (response.ok) {
                    alert('User deleted successfully');
                    // Optimistically remove user from state for immediate feedback
                    setB2cUsers(prev => prev.filter(user => user.id !== userId));
                    setB2bUsers(prev => prev.filter(user => user.id !== userId));
                } else {
                    const data = await response.json();
                    alert(data.error || 'Failed to delete user');
                }
            } catch (error) {
                console.error('Error deleting user:', error);
                alert('An error occurred while deleting the user');
            }
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    <h6 className="text-lg fw-semibold mb-0">User Management</h6>
                    <ul className="nav nav-pills border-gradient-tab" id="pills-tab" role="tablist">
                        <li className="nav-item" role="presentation">
                            <button className="nav-link active" id="pills-b2c-tab" data-bs-toggle="pill" data-bs-target="#pills-b2c" type="button" role="tab">
                                B2C Users
                            </button>
                        </li>
                        <li className="nav-item" role="presentation">
                            <button className="nav-link" id="pills-b2b-tab" data-bs-toggle="pill" data-bs-target="#pills-b2b" type="button" role="tab">
                                B2B Users (Business)
                            </button>
                        </li>
                    </ul>
                </div>
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <form className="navbar-search">
                        <input type="text" className="bg-base h-40-px w-auto" name="search" placeholder="Search Users" />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="tab-content" id="pills-tabContent">
                    {/* B2C Users Tab */}
                    <div className="tab-pane fade show active" id="pills-b2c" role="tabpanel">
                        {loading ? 'Loading...' : (
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">User</th>
                                            <th scope="col">Contact</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {b2cUsers.map(user => (
                                            <tr key={user.id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-grow-1">
                                                            <h6 className="text-md mb-0 fw-medium">{user.full_name}</h6>
                                                            <span className="text-sm text-secondary-light fw-medium">ID: #{user.id}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        <span className="text-sm">{user.email}</span>
                                                        <span className="text-sm text-secondary-light">{user.phone}</span>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span className={`badge px-24 py-4 rounded-pill fw-medium text-sm ${
                                                        user.status === 'active' ? 'bg-success-focus text-success-main' : 
                                                        user.status === 'banned' ? 'bg-danger-focus text-danger-main' : 'bg-warning-focus text-warning-main'
                                                    }`}>
                                                        {user.status}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="dropdown">
                                                        <button className="btn btn-sm btn-outline-primary radius-8 px-14 py-6 d-flex align-items-center gap-1" type="button" data-bs-toggle="dropdown">
                                                            Action <Icon icon="iconamoon:arrow-down-2-duotone" />
                                                        </button>
                                                        <ul className="dropdown-menu">
                                                            <li>
                                                                <button 
                                                                    className={`dropdown-item ${user.status === 'banned' ? 'text-success' : 'text-warning'}`}
                                                                    onClick={() => handleBan(user)}
                                                                >
                                                                    {user.status === 'banned' ? 'Unban User' : 'Ban User'}
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button 
                                                                    className="dropdown-item text-danger" 
                                                                    onClick={() => handleDelete(user.id)}
                                                                >
                                                                    Delete User
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* B2B Users Tab */}
                    <div className="tab-pane fade" id="pills-b2b" role="tabpanel">
                        {loading ? 'Loading...' : (
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr>
                                            <th scope="col">Business Account</th>
                                            <th scope="col">Owner</th>
                                            <th scope="col">Venues</th>
                                            <th scope="col">Promotions (Impressions)</th>
                                            <th scope="col" className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {b2bUsers.map(user => (
                                            <tr key={user.id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-grow-1">
                                                            <h6 className="text-md mb-0 fw-medium">{user.email}</h6>
                                                            <span className="text-sm text-secondary-light fw-medium">Reg: {new Date(user.created_at).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{user.full_name}</td>
                                                <td>{user.venue_count} Venues</td>
                                                <td>{user.total_impressions}</td>
                                                <td className="text-center">
                                                    <div className="dropdown">
                                                        <button className="btn btn-sm btn-outline-primary radius-8 px-14 py-6 d-flex align-items-center gap-1" type="button" data-bs-toggle="dropdown">
                                                            Action <Icon icon="iconamoon:arrow-down-2-duotone" />
                                                        </button>
                                                        <ul className="dropdown-menu">
                                                            <li>
                                                                <button 
                                                                    className={`dropdown-item ${user.status === 'banned' ? 'text-success' : 'text-warning'}`}
                                                                    onClick={() => handleBan(user)}
                                                                >
                                                                    {user.status === 'banned' ? 'Unban User' : 'Ban User'}
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button 
                                                                    className="dropdown-item text-danger" 
                                                                    onClick={() => handleDelete(user.id)}
                                                                >
                                                                    Delete User
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserManagementLayer;
